"use client";

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import {
  DEFAULT_LOCALE,
  LOCALE_FLAGS,
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  isLocalizedPseoPath,
  localePath,
  normalizeLocale,
} from '@/lib/siteI18n';
import { hasLocalizedPseoPath } from '@/lib/pseoAvailability';
import { landingAnalyticsAllowed, sendLandingEvent } from '@/lib/analytics';

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const sendLanguageEvent = (eventName, locale, params = {}) => {
  sendLandingEvent(eventName, {
    language: locale,
    page_path: window.location.pathname,
    ...params,
  });
};

const setLanguageUserProperty = (locale) => {
  if (!landingAnalyticsAllowed() || typeof window.gtag !== 'function') return;
  try { window.gtag('set', 'user_properties', {
    landing_language: locale,
  }); } catch {}
};

function stripLocale(pathname) {
  const parts = String(pathname || '/').split('/');
  const first = parts[1];
  if (SUPPORTED_LOCALES.includes(first)) {
    const stripped = `/${parts.slice(2).join('/')}`.replace(/\/$/, '');
    return stripped || '/';
  }
  return pathname || '/';
}

function targetPath(pathname, locale) {
  const base = stripLocale(pathname);
  if (isLocalizedPseoPath(base) && !hasLocalizedPseoPath(base, locale)) {
    const section = base.split('/').filter(Boolean)[0];
    return localePath(`/${section}`, locale);
  }
  return localePath(base, locale);
}

export function persistLocale(locale) {
  const normalized = normalizeLocale(locale);
  try {
    window.localStorage.setItem('it_locale', normalized);
  } catch (_) {}
  document.cookie = `it_locale=${normalized}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

export default function LanguageSwitcher({ locale = DEFAULT_LOCALE, label = 'Language', compact = false }) {
  const pathname = usePathname();
  const current = normalizeLocale(locale);

  useEffect(() => {
    setLanguageUserProperty(current);
    sendLanguageEvent('landing_language_view', current);
    sendLanguageEvent(`landing_language_${current}`, current);
  }, [current]);

  const handleChange = (event) => {
    const next = normalizeLocale(event.target.value);
    sendLanguageEvent('landing_language_change', next, {
      previous_language: current,
    });
    sendLanguageEvent(`landing_language_change_${next}`, next, {
      previous_language: current,
    });
    persistLocale(next);
    window.location.href = targetPath(pathname, next);
  };

  return (
    <label className={`language-switcher ${compact ? 'compact' : ''}`}>
      <span className="language-switcher__label">{label}</span>
      <span className="language-switcher__control">
        {compact && (
          <span className="language-switcher__flag" aria-hidden="true">
            {LOCALE_FLAGS[current]}
          </span>
        )}
        <select value={current} onChange={handleChange} aria-label={label}>
          {SUPPORTED_LOCALES.map((code) => (
            <option key={code} value={code} aria-label={LOCALE_LABELS[code]} title={LOCALE_LABELS[code]}>
              {compact ? LOCALE_FLAGS[code] : LOCALE_LABELS[code]}
            </option>
          ))}
        </select>
      </span>
    </label>
  );
}
